import { useMemo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import { useSKUsTableRows } from "../../../../pages/system-settings/tabs/skus/hooks/useSKUsTableRows";

interface SKU {
  id: string;
  skuNumber: string;
  skuName: string;
  category: string;
  skuData: string;
  nodeCount: number;
}

interface ISKUSelect {
  skuId?: string | null;
  label?: string;
  onSKUChange: (sku: SKU) => void;
  error?: boolean;
  helperText?: string;
}

interface ISKUOption {
  title: string;
  value: SKU;
}

interface SKURow {
  id?: string;
  skuNumber?: string;
  skuName?: string;
  category?: string | null;
  skuData?: string;
  nodeCount?: number;
}

const SKUSelect = ({
  skuId,
  label,
  onSKUChange,
  error = false,
  helperText = "",
}: ISKUSelect): JSX.Element => {
  const { rows, loading } = useSKUsTableRows();

  const options = useMemo((): ISKUOption[] => {
    return (
      rows?.map((item: SKURow): ISKUOption => {
        // Display format: "SKU Number - SKU Name (Category)"
        const categoryPart = item.category ? ` (${item.category})` : "";
        const displayTitle = `${item.skuNumber} - ${item.skuName}${categoryPart}`;

        return {
          title: displayTitle,
          value: {
            id: item.id ?? "",
            skuNumber: item.skuNumber ?? "",
            skuName: item.skuName ?? "",
            category: item.category ?? "",
            skuData: item.skuData ?? "",
            nodeCount: item.nodeCount ?? 1,
          },
        };
      }) ?? []
    );
  }, [rows]);

  const memoizedValue = useMemo((): ISKUOption | null => {
    if (!skuId) {
      return null;
    }

    return options.find(option => option.value.skuNumber === skuId) ?? null;
  }, [skuId, options]);

  return (
    <Autocomplete
      fullWidth
      disableClearable
      loading={loading}
      value={memoizedValue as ISKUOption}
      onChange={(_event, newValue: ISKUOption | null): void => {
        if (newValue) {
          onSKUChange(newValue.value);
        }
      }}
      getOptionLabel={(option: ISKUOption): string => option?.title ?? ""}
      isOptionEqualToValue={(option, optionValue): boolean => {
        return option?.value?.id === optionValue?.value?.id;
      }}
      options={options}
      renderInput={(params): JSX.Element => (
        <TextField
          {...params}
          label={label ?? "SKU"}
          error={error}
          helperText={error ? helperText : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SKUSelect;
